import {
  CodeOutlined,
  InfoCircleOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Descriptions,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { CardComponent } from 'components/card/CardComponent';
import { useMemo, useState } from 'react';
import { CheckerEmployee, Device } from 'types/devices';
import { ModalUserContent } from './DeviceCommands/ModalUserContent';
import { addCommand, bulkCommand } from 'api/devices.api';
import { ModalGetBiometricContent } from './DeviceCommands/ModalGetBiometricContent';
import { CustomCommandBuilder } from './DeviceCommands/CustomCommandBuilder';
import { format, isValid } from 'date-fns';
import { getDeviceProtocolVersion } from 'libs/deviceProtocol';
import { getDevicePrivilegePresentation } from 'libs/devicePrivilege';

interface DetailProps {
  device: Device;
  employees: CheckerEmployee[];
  employeesLoading: boolean;
  employeesError?: string;
  onEmployeesRefresh: () => Promise<void>;
}

//DATA QUERY tablename=user,fielddesc=*,filter =*

export const DeviceDetail = ({
  device,
  employees,
  employeesLoading,
  employeesError,
  onEmployeesRefresh,
}: DetailProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [openModalBio, setOpenModalBio] = useState(false);
  const [openCommandBuilder, setOpenCommandBuilder] = useState(false);
  const [employeePinFilter, setEmployeePinFilter] = useState('');

  const showModal = () => {
    setOpenModal(true);
  };
  const showModalBio = () => {
    setOpenModalBio(true);
  };

  const handleCancel = () => {
    setOpenModal(false);
    setOpenModalBio(false);
    setOpenCommandBuilder(false);
  };

  const queryDataHandler = async () => {
    await addCommand({
      serialNumber: device.serialNumber,
      command: 'queryData',
      tableName: 'user',
    });
  };

  const queryLogHandler = async () => {
    await addCommand({
      serialNumber: device.serialNumber,
      command: 'queryData',
      tableName: 'transaction',
    });
  };

  const queryGetBiometrics = async (pin: number) => {
    const protocolVersion = getDeviceProtocolVersion(device);
    if (!protocolVersion) return;
    const commands =
      protocolVersion === 2
        ? [
            {
              serialNumber: device.serialNumber,
              command: 'getFinger' as const,
              employeeId: pin,
            },
            ...Array.from({ length: 11 }, (_, bioType) => ({
              serialNumber: device.serialNumber,
              command: 'getBioData' as const,
              employeeId: pin,
              bioType,
            })),
          ]
        : (['getFinger', 'getBioData', 'getBioPhoto'] as const).map(
            (command) => ({
              serialNumber: device.serialNumber,
              command,
              employeeId: pin,
            })
          );
    await bulkCommand(commands);
  };

  const queryRebootHandeler = async () => {
    await addCommand({
      serialNumber: device.serialNumber,
      command: 'reboot',
    });
  };

  const filteredEmployees = useMemo(() => {
    const normalizedFilter = employeePinFilter.trim();
    if (!normalizedFilter) return employees;
    return employees.filter((employee) =>
      String(employee.PIN).includes(normalizedFilter)
    );
  }, [employeePinFilter, employees]);

  const protocolVersion = getDeviceProtocolVersion(device);

  const columns = [
    {
      title: 'PIN',
      dataIndex: 'PIN',
      key: 'pin',
      defaultSortOrder: 'ascend' as const,
      sorter: (first: CheckerEmployee, second: CheckerEmployee) =>
        Number(first.PIN) - Number(second.PIN),
    },
    {
      title: 'Nombre',
      dataIndex: 'Name',
      key: 'name',
    },
    {
      title: (
        <Space size={4}>
          <span>Rol en el dispositivo</span>
          <Tooltip title="0: Usuario normal · 2: Registrador · 6: Administrador · 10: Personalizado · 14: Superadministrador">
            <InfoCircleOutlined aria-label="Ayuda sobre roles del dispositivo" />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'Pri',
      key: 'privilege',
      render: (rawPrivilege: CheckerEmployee['Pri']) => {
        const role = getDevicePrivilegePresentation(rawPrivilege);
        if (!role.rawValue) {
          return (
            <Typography.Text type="secondary">{role.label}</Typography.Text>
          );
        }
        return (
          <Space size={6} wrap>
            <Tag
              color={
                role.administrator ? 'green' : role.known ? 'blue' : 'gold'
              }
            >
              Pri {role.rawValue}
            </Tag>
            <Typography.Text type={role.known ? undefined : 'warning'}>
              {role.label}
            </Typography.Text>
          </Space>
        );
      },
    },
    {
      title: 'Última checada en este dispositivo',
      dataIndex: 'lastCheckIn',
      key: 'lastCheckIn',
      sorter: (first: CheckerEmployee, second: CheckerEmployee) => {
        const firstDate = first.lastCheckIn
          ? new Date(first.lastCheckIn).getTime()
          : 0;
        const secondDate = second.lastCheckIn
          ? new Date(second.lastCheckIn).getTime()
          : 0;
        return firstDate - secondDate;
      },
      render: (lastCheckIn?: string) => {
        if (!lastCheckIn) return 'Sin checadas';
        const checkDate = new Date(lastCheckIn);
        return isValid(checkDate)
          ? format(checkDate, 'dd/MM/yyyy HH:mm:ss')
          : 'Sin checadas';
      },
    },
    {
      title: 'Opciones',
      dataIndex: 'options',
      key: 'options',
      render: (_text: string, e: CheckerEmployee) => (
        <>
          <Button onClick={() => queryGetBiometrics(e.PIN)}>
            Obtener Biometricos
          </Button>
        </>
      ),
    },
  ];

  return (
    <div id="device-detail-container">
      <CardComponent
        title="Detalle del dispositivo"
        content={
          <>
            <Descriptions title="" layout="horizontal" bordered>
              <Descriptions.Item label="Número de Serie" span={3}>
                {device.serialNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Nombre del dispositivo" span={3}>
                {device.configs.DeviceName}
              </Descriptions.Item>
              <Descriptions.Item label="Etiquetas" span={3}>
                <Space wrap data-cy="device-detail-tags">
                  {device.tags?.length ? (
                    device.tags.map((tag) => (
                      <Tag key={tag._id} color={tag.tagColor} variant="solid">
                        {tag.tagName}
                      </Tag>
                    ))
                  ) : (
                    <Typography.Text type="secondary">
                      Sin etiquetas
                    </Typography.Text>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Firmware Ver." span={3}>
                {device.properties.FWVersion}
              </Descriptions.Item>
              <Descriptions.Item label="IP" span={3}>
                {device.properties.IPAddress}
              </Descriptions.Item>
              <Descriptions.Item label="Push Ver. reportada" span={3}>
                {device.properties.PushVersion || 'Sin reporte'}
              </Descriptions.Item>
              <Descriptions.Item label="Protocolo activo" span={3}>
                {protocolVersion
                  ? `v${protocolVersion} (${device.pushVer}, ${
                      device.pushVerSource === 'manual' ? 'manual' : 'handshake'
                    })`
                  : 'Sin configurar'}
              </Descriptions.Item>
              <Descriptions.Item label="Modo" span={3}>
                {device.deviceType}
              </Descriptions.Item>
            </Descriptions>
            <Space style={{ marginRight: 6 }}>
              <Button
                title="Construir comando personalizado"
                icon={<CodeOutlined />}
                onClick={() => setOpenCommandBuilder(true)}
                data-cy="open-custom-command-builder"
              >
                Comando personalizado
              </Button>
            </Space>
            <Space style={{ marginRight: 6 }}>
              <span title="Actualizar empleados">
                <Button
                  title="Actualizar empleados"
                  icon={
                    <UserAddOutlined
                      style={{ color: 'var(--status-success-text-color)' }}
                      onClick={() => {
                        showModal();
                      }}
                    />
                  }
                />
              </span>
            </Space>
            <Space style={{ marginRight: 6 }}>
              <Button
                title="descarga biometricos"
                onClick={() => showModalBio()}
              >
                Descargar biometricos
              </Button>
            </Space>
            <Space style={{ marginRight: 6 }}>
              <Button title="reinicia" onClick={() => queryRebootHandeler()}>
                Reiniciar
              </Button>
            </Space>
            <Space style={{ marginRight: 6 }}>
              <span>
                <Button onClick={() => queryDataHandler()}>
                  Descargar Empleados
                </Button>
              </span>
            </Space>
            {/* <Space style={{ marginRight: 6 }}>
              <span><Button onClick={() => queryBiometricHandler()}>Descargar Biometricos</Button></span>
            </Space> */}
            <Space style={{ marginRight: 6 }}>
              <span>
                <Button onClick={() => queryLogHandler()}>
                  Descargar Logs
                </Button>
              </span>
            </Space>
            <div
              className="mt-5 mb-3"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
            >
              <div
                data-cy="device-employee-pin-filter"
                style={{ flex: '1 1 280px', maxWidth: 420, minWidth: 0 }}
              >
                <Input.Search
                  allowClear
                  value={employeePinFilter}
                  placeholder="Buscar por número de empleado (PIN)"
                  onChange={({ target }) => setEmployeePinFilter(target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <Button
                loading={employeesLoading}
                disabled={employeesLoading}
                onClick={() => void onEmployeesRefresh()}
                data-cy="refresh-device-employees"
              >
                Actualizar lista
              </Button>
            </div>
            {employeesError && (
              <Alert
                type="error"
                showIcon
                title={employeesError}
                role="alert"
                className="mb-3"
                data-cy="device-employees-error"
              />
            )}
            <Table
              rowKey="PIN"
              loading={employeesLoading}
              dataSource={filteredEmployees}
              columns={columns}
              pagination={{
                defaultPageSize: 25,
                showSizeChanger: true,
                pageSizeOptions: [25, 50, 100, 200],
                showTotal: (total) => `${total} empleados`,
              }}
              scroll={{ x: 1050 }}
              data-cy="device-employees-table"
            />
          </>
        }
      />
      <Modal
        title="Constructor de comandos"
        open={openCommandBuilder}
        destroyOnHidden
        width={720}
        footer={null}
        onCancel={handleCancel}
      >
        <CustomCommandBuilder
          device={device}
          employees={employees}
          onEmployeesRefresh={onEmployeesRefresh}
          onClose={handleCancel}
        />
      </Modal>
      <Modal
        title="Actualizar empleados"
        open={openModal}
        destroyOnHidden
        width="90vw"
        okButtonProps={{ disabled: true, style: { display: 'none' } }}
        onCancel={handleCancel}
      >
        <ModalUserContent deviceInfo={device} closeModal={handleCancel} />
      </Modal>
      <Modal
        title="Obtener biometricos del dispositivo"
        open={openModalBio}
        destroyOnHidden
        width="90vw"
        okButtonProps={{ disabled: true, style: { display: 'none' } }}
        onCancel={handleCancel}
      >
        <ModalGetBiometricContent
          deviceInfo={device}
          closeModal={handleCancel}
        />
      </Modal>
    </div>
  );
};
